import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Tag from '../ui/Tag';
import { calculateWorkingHours, compareCalculationMethods } from '../../core/TimeCalculator';
import './RestForm.css';

const RestForm = ({ 
  onSubmit, 
  onCancel, 
  availableHours = { overtime: 0, annualLeave: 0 },
  className = '' 
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    startTime: '',
    endTime: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [allocationPreview, setAllocationPreview] = useState(null);

  // 处理输入变化
  const handleInputChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // 清除相关错误
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // 实时预览计算结果
    if (newFormData.startTime && newFormData.endTime) {
      try {
        const comparison = compareCalculationMethods(newFormData.startTime, newFormData.endTime);
        setPreviewData(comparison);
        
        // 计算时间分配预览
        const requiredHours = comparison.working.hours;
        const allocation = calculateAllocation(requiredHours);
        setAllocationPreview(allocation);
      } catch (error) {
        setPreviewData(null);
        setAllocationPreview(null);
      }
    } else {
      setPreviewData(null);
      setAllocationPreview(null);
    }
  };

  // 计算时间分配
  const calculateAllocation = (requiredHours) => {
    const totalAvailable = availableHours.overtime + availableHours.annualLeave;
    
    if (totalAvailable < requiredHours) {
      return {
        isValid: false,
        shortage: requiredHours - totalAvailable,
        allocation: null
      };
    }

    // 优先使用加班时间
    const fromOvertime = Math.min(requiredHours, availableHours.overtime);
    const fromAnnualLeave = requiredHours - fromOvertime;

    return {
      isValid: true,
      shortage: 0,
      allocation: {
        fromOvertime,
        fromAnnualLeave,
        total: requiredHours
      }
    };
  };

  // 验证表单
  const validateForm = () => {
    const newErrors = {};

    if (!formData.startTime) {
      newErrors.startTime = t('rest.validation.startTimeRequired');
    }

    if (!formData.endTime) {
      newErrors.endTime = t('rest.validation.endTimeRequired');
    }

    if (formData.startTime && formData.endTime) {
      const startTime = new Date(formData.startTime);
      const endTime = new Date(formData.endTime);

      if (endTime <= startTime) {
        newErrors.endTime = t('rest.validation.endTimeAfterStart');
      }

      const hours = calculateWorkingHours(startTime, endTime);
      if (hours <= 0) {
        newErrors.endTime = '调休时间必须大于0小时';
      }

      // 检查可用时间是否足够
      const totalAvailable = availableHours.overtime + availableHours.annualLeave;
      if (hours > totalAvailable) {
        newErrors.endTime = t('rest.validation.insufficientTime');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 处理提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        startTime: formData.startTime,
        endTime: formData.endTime
      });
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 处理取消
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className={`rest-form ${className}`}>
      <div className="rest-form__header">
        <h3 className="rest-form__title">
          {t('rest.record')}
        </h3>
        
        {/* 可用时间显示 */}
        <div className="rest-form__available">
          <div className="rest-form__available-item">
            <span className="rest-form__available-label">可用加班时间：</span>
            <Tag variant="success" size="sm">
              {availableHours.overtime} 小时
            </Tag>
          </div>
          <div className="rest-form__available-item">
            <span className="rest-form__available-label">可用年假时间：</span>
            <Tag variant="info" size="sm">
              {availableHours.annualLeave} 小时
            </Tag>
          </div>
          <div className="rest-form__available-item">
            <span className="rest-form__available-label">总可用时间：</span>
            <Tag variant="primary" size="sm">
              {availableHours.overtime + availableHours.annualLeave} 小时
            </Tag>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rest-form__form">
        {/* 开始时间 */}
        <div className="rest-form__field">
          <Input
            type="datetime-local"
            label={t('rest.startTime')}
            value={formData.startTime}
            onChange={(value) => handleInputChange('startTime', value)}
            error={errors.startTime}
            required
            className="rest-form__input"
          />
        </div>

        {/* 结束时间 */}
        <div className="rest-form__field">
          <Input
            type="datetime-local"
            label={t('rest.endTime')}
            value={formData.endTime}
            onChange={(value) => handleInputChange('endTime', value)}
            error={errors.endTime}
            required
            className="rest-form__input"
          />
        </div>

        {/* 实时预览 */}
        {previewData && (
          <div className="rest-form__preview">
            <h4 className="rest-form__preview-title">计算预览</h4>
            <div className="rest-form__preview-content">
              <div className="rest-form__preview-item">
                <span className="rest-form__preview-label">时间段：</span>
                <Tag variant="time" size="sm">
                  {previewData.startTime} - {previewData.endTime}
                </Tag>
              </div>
              
              <div className="rest-form__preview-item">
                <span className="rest-form__preview-label">调休时间：</span>
                <Tag variant="result" size="sm">
                  {previewData.working.hours} 小时
                </Tag>
                <Tag variant="note" size="sm">
                  工作时间，扣除午休
                </Tag>
              </div>
              
              <div className="rest-form__preview-item">
                <span className="rest-form__preview-label">如果加班：</span>
                <Tag variant="result" size="sm">
                  {previewData.overtime.hours} 小时
                </Tag>
                <Tag variant="note" size="sm">
                  连续时间，包含午休
                </Tag>
              </div>
            </div>
          </div>
        )}

        {/* 时间分配预览 */}
        {allocationPreview && (
          <div className={`rest-form__allocation ${!allocationPreview.isValid ? 'rest-form__allocation--error' : ''}`}>
            <h4 className="rest-form__allocation-title">时间分配预览</h4>
            
            {allocationPreview.isValid ? (
              <div className="rest-form__allocation-content">
                {allocationPreview.allocation.fromOvertime > 0 && (
                  <div className="rest-form__allocation-item">
                    <span className="rest-form__allocation-label">使用加班时间：</span>
                    <Tag variant="success" size="sm">
                      {allocationPreview.allocation.fromOvertime} 小时
                    </Tag>
                  </div>
                )}
                
                {allocationPreview.allocation.fromAnnualLeave > 0 && (
                  <div className="rest-form__allocation-item">
                    <span className="rest-form__allocation-label">使用年假时间：</span>
                    <Tag variant="info" size="sm">
                      {allocationPreview.allocation.fromAnnualLeave} 小时
                    </Tag>
                  </div>
                )}
                
                <div className="rest-form__allocation-item">
                  <span className="rest-form__allocation-label">总计：</span>
                  <Tag variant="primary" size="sm">
                    {allocationPreview.allocation.total} 小时
                  </Tag>
                </div>
              </div>
            ) : (
              <div className="rest-form__allocation-error">
                <Tag variant="danger" size="sm">
                  时间不足 {allocationPreview.shortage} 小时
                </Tag>
                <span className="rest-form__allocation-error-text">
                  请减少调休时间或增加加班时间/年假
                </span>
              </div>
            )}
          </div>
        )}

        {/* 提交错误 */}
        {errors.submit && (
          <div className="rest-form__error">
            {errors.submit}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="rest-form__actions">
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting || (allocationPreview && !allocationPreview.isValid)}
          >
            {t('rest.record')}
          </Button>
          
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RestForm;
